import { PermissionOptions } from 'constants/Administrators';
import { BenefitStatus } from 'store/benefits';
import { EmbeddedCustomCategory } from 'store/custom-categories';
import { z } from 'zod';
import { AssignmentStatus } from 'store/assignments';
import { ContentLanguage, TimeUnit } from 'types/general';
import {
  employeeExpensesFiltersSchema,
  employeeJobTitlesFiltersSchema,
  employeesFiltersSchema,
  employeeWalletBenefitsFiltersSchema,
} from './employees.schemas';

export const employmentStatuses = [
  'recentlyEnded',
  'ended',
  'active',
  'endsSoon',
  'inactive',
] as const;
export type EmploymentStatus = (typeof employmentStatuses)[number];

export const employeeStatuses = ['active', 'inactive'] as const;
export type EmployeeStatus = (typeof employeeStatuses)[number];

export const employmentTypes = ['fullTime', 'partTime', 'intern'] as const;
export type EmploymentType = (typeof employmentTypes)[number];

export const employeeCognitoStatuses = [
  'linked',
  'missing',
  'unlinked',
  'unavailable',
] as const;
export type EmployeeCognitoStatus = (typeof employeeCognitoStatuses)[number];

export interface EmployeeCognitoUser {
  username: string;
  sub: string;
  email: string | null;
  phone: string | null;
  userStatus: string;
}

export interface EmployeeCognito {
  status: EmployeeCognitoStatus;
  user: EmployeeCognitoUser | null;
}

export interface BaseEmployee {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  hireDate: string | null;
  consents: {
    shareEmail: boolean;
    sharePhoneNumber: boolean;
    shareBirthdate: boolean;
  };
}

export interface ListEmployee extends BaseEmployee {
  employmentStatus: EmploymentStatus;
  birthday: string | null;
  isAppUser: boolean;
  customCategories: EmbeddedCustomCategory[];
}

export interface Employee extends BaseEmployee {
  parentCompanyId: string;
  dateOfBirth: string | null;
  internalComment: string;
  personalCode: string;
  jobTitle: string;
  employeeId: string;
  externalId: string;
  iban: string;
  avatarId: string | null;
  capacity: EmploymentType;
  endDate: string | null;
  startDate: string | null;
  endTime: string | null;
  startDateTZ: number;
  endDateTZ: number;
  startTime: string | null;
  userId: string;
  companyId: string;
  departmentId: string;
  teamId: string;
  language: Uppercase<ContentLanguage>;
  status: EmployeeStatus;
  temporaryEmployment: boolean;
  customCategories: EmbeddedCustomCategory[];
  permission: PermissionOptions;
  employmentDateTZ: number;
  employmentTime: string;
  employmentDuration: Partial<Record<TimeUnit, number>> | null;
  user: {
    id: string;
    lastActionDate: string;
  } | null;
  cognito: EmployeeCognito | null;
}

export interface EmployeeJobTitle {
  name: string;
}

export interface EmployeeWalletBenefit {
  id: string;
  name: string;
  status: BenefitStatus;
  assignmentStatus: AssignmentStatus;
  investment: number;
  expenses: number;
  remaining: number;
}

export interface EmployeeWalletRecognitions {
  totalPoints: number;
  totalCurrency: number;
  spentPoints: number;
  spentCurrency: number;
  availablePoints: number;
  availableCurrency: number;
  expiredPoints: number;
  expiredCurrency: number;
}

export type EmployeesFilters = z.infer<typeof employeesFiltersSchema>;

export type EmployeeJobTitlesFilters = z.infer<
  typeof employeeJobTitlesFiltersSchema
>;

export type EmployeeExpensesFilters = z.infer<
  typeof employeeExpensesFiltersSchema
>;

export type EmployeeWalletBenefitsFilters = z.infer<
  typeof employeeWalletBenefitsFiltersSchema
>;
